import React from 'react'
import {strftime} from '@fotolog/xutil'
import {useSuspense} from '@data-client/react'
import {Trip, PublicTripResource, TripsFilter} from '../lib/model'
import {EpicMap} from '../components/Map'
import {PageProps} from '../components/PageRouter'
import {ILatLng} from '@fotolog/data'
import {useWindowSize} from 'react-use'
import {useForm, useToggle, val2opt} from '../lib'
import {DetailPanel, Field, SelectTags, SubmitButton} from '../components'
import {FilterTripsForm} from '../lib/form/Trip'
import {FilterTripsSchema} from '../lib/schema/Trip'
import {Box, Button, Container, Grid, Paper, Typography} from '@mui/material'
import {IconDelete, IconTripMap, IconClose, IconFilters} from '../Icons'
import {useLocation} from 'wouter'
import {PublicGlobeTrips} from '../components/Trip'

function defaultTripFilters(scope: number): TripsFilter {
  const stored = localStorage.getItem('trips.filter')
  if (stored) {
    try {
      const d = JSON.parse(stored) as TripsFilter
      if (d.scope === scope) {
        // only if saved filter matches current scope
        return d
      }
    } catch {
      console.log('resetting unsupported trips.filter')
      localStorage.removeItem('trips.filter')
    }
  }
  return {scope, tags: []}
}

export default function PublicGlobePage({session}: PageProps) {
  const {width} = useWindowSize()
  const [location] = useLocation()
  const detailing = location.match('/globe/trip/') // TODO: awkward coupling with route
  const right = detailing ? 400 : 0 // TODO: use PANEL_WIDTH constant
  const filters = useToggle(false)
  const [bounds, setBounds] = React.useState(JSON.parse(localStorage.getItem('trip.bounds') || 'null'))
  const onMap = (trip: Trip) => {
    const gps = trip.cover?.gps
    if (!bounds || !gps) {
      return true
    }
    const [bl, ur] = bounds
    return bl.lat <= gps.lat && gps.lat <= ur.lat && bl.lng <= gps.lng && gps.lng <= ur.lng
  }
  const onBoundsChange = (bl: ILatLng, ur: ILatLng) => {
    setBounds([bl, ur])
    localStorage.setItem('trip.bounds', JSON.stringify([bl, ur]))
  }
  const [qry, updateQry] = React.useState<TripsFilter>({scope: session.euid(), tags: []})
  React.useEffect(() => {
    const fields = defaultTripFilters(session.euid())
    form.setValue('start', fields.start ? new Date(fields.start) : null)
    form.setValue('end', fields.end ? new Date(fields.end) : null)
    if (fields.tags) {
      form.setValue('tags', fields.tags.map(val2opt))
    }
    updateQry(fields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const form = useForm(FilterTripsSchema)
  const trips = useSuspense(PublicTripResource.getList, qry)

  const onClearFilters = () => {
    localStorage.removeItem('trips.filter')
    form.setValue('start', null)
    form.setValue('end', null)
    form.setValue('tags', [])
    updateQry({scope: session.euid(), tags: []})
  }

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSearch = form.handleSubmit(async (data: FilterTripsSchema) => {
    const doit: TripsFilter = {
      scope: session.euid(),
      tags: [],
    }
    if (data.tags) {
      doit.tags = data.tags.map(i => i.value)
    }
    if (data.start) {
      doit.start = Number(strftime(data.start, 'T', {utc: true}))
    }
    if (data.end) {
      doit.end = Number(strftime(data.end, 'T', {utc: true}))
    }
    updateQry(doit)
    localStorage.setItem('trips.filter', JSON.stringify(doit))
    filters.off()
  })

  if (!trips) {
    return null
  }
  return (
    <Box position="fixed" top={80} left={0} bottom={0} right={right}>
      <EpicMap trips={trips} width={width - right} onBoundsChange={onBoundsChange} />
      {detailing || (
        <Container maxWidth="md" sx={{position: 'relative'}}>
          <Box className="app-flex" flexWrap="wrap" mt="10px" bgcolor="rgba(50, 50, 50, 0.5)" borderRadius={1}>
            <Typography variant="h3" color="secondary" p={1}>
              Public Trips
            </Typography>
            <Box className="app-flex" mr={3}>
              <Button title="List Trips" href="./trip/" color="primary" variant="contained" size="small">
                <IconTripMap />
              </Button>
              &nbsp;
              <Button title="Filter Trips" onClick={filters.toggle} color="primary" variant="contained" size="small">
                <IconFilters />
              </Button>
            </Box>
          </Box>
          {filters.enabled && (
            <Paper elevation={3} sx={{p: 2, mt: 1}}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h3">Filters</Typography>

                <IconClose onClick={filters.off} className="app-clickable" color="primary" fontSize="large" />
              </Box>
              <form onSubmit={onSearch}>
                <Grid container columns={4} spacing={4}>
                  <Grid size={{xs: 4, sm: 2, md: 1}}>
                    <Field form={form} attrs={FilterTripsForm.start} />
                  </Grid>
                  <Grid size={{xs: 4, sm: 2, md: 1}}>
                    <Field form={form} attrs={FilterTripsForm.end} />
                  </Grid>
                  <Grid size={{xs: 4, md: 2}}>
                    <SelectTags form={form} attrs={FilterTripsForm.tags} tags={session.scope.tags} />
                  </Grid>
                </Grid>
                <Box display="flex" justifyContent="center" flexDirection={{xs: 'column', sm: 'row'}} mt={3}>
                  <Button color="error" onClick={onClearFilters}>
                    <IconDelete /> &nbsp; Clear Filters
                  </Button>
                  <div style={{width: 40, height: 20}} />
                  <SubmitButton form={form}>Apply Filters</SubmitButton>
                </Box>
              </form>
            </Paper>
          )}
        </Container>
      )}
      <DetailPanel
        props={{trips: trips.filter(onMap)}}
        path="(?<home>/globe)/trip/(?<trip>\d+)?$"
        panel={PublicGlobeTrips}
        title={['Globe', 'Matching Trips']}
        overlay={false}
      />
    </Box>
  )
}
